import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, Mail, Shield, Key, Lock, Loader2, Pencil } from "lucide-react";
import { useApiToken } from "@/components/providers/token-provider";
import { useState, useEffect } from "react";
import { authApi } from "@/api/services";
import { toast } from "sonner";
import { ChangePasswordCard } from "./profile/ChangePasswordCard";
import { ApiKeyManagement } from "./profile/ApiKeyManagement";

export default function ProfilePage() {
  const { user, organization, isTokenLoaded, refreshUser } = useApiToken();
  // SSO-only orgs delegate password + MFA to the upstream IdP — hide
  // those settings entirely. The matching backend endpoints already
  // return 403 for sso_only orgs, but the UI shouldn't surface them.
  const isSSOOnlyOrg = organization?.auth_mode === "sso_only";

  const [mfaEnabled, setMfaEnabled] = useState<boolean | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableOtp, setDisableOtp] = useState("");

  const [isEditingName, setIsEditingName] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nameLoading, setNameLoading] = useState(false);

  useEffect(() => {
    // Skip the MFA status fetch for sso_only orgs — the endpoint returns
    // 403, and we won't render the card anyway.
    if (isTokenLoaded && !isSSOOnlyOrg) {
      checkMfaStatus();
    }
  }, [isTokenLoaded, isSSOOnlyOrg]);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
    }
  }, [user]);

  const handleEditName = () => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
  };

  const handleSaveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First name and last name cannot be empty");
      return;
    }
    setNameLoading(true);
    try {
      await authApi.updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      await refreshUser();
      setIsEditingName(false);
      toast.success("Profile updated successfully");
    } catch {
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setNameLoading(false);
    }
  };

  const checkMfaStatus = async () => {
    try {
      const res = await authApi.getMfaStatus();
      setMfaEnabled(res.enabled);
    } catch (e) {
      console.error("Failed to check MFA status", e);
      // Default to "not enabled" so the user sees the actionable Enable MFA
      // button instead of a perpetual skeleton. If MFA actually IS configured
      // server-side, the setup flow surfaces that on attempt.
      setMfaEnabled(false);
    }
  };

  const handleEnableMfa = async () => {
    setMfaLoading(true);
    try {
      const res = await authApi.setupMfa();
      toast.success("Setup email sent", {
        description: res.message,
      });
    } catch (e) {
      toast.error("Failed to initiate MFA setup");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!disablePassword || !disableOtp) {
      toast.error("Please enter both password and OTP code");
      return;
    }
    setMfaLoading(true);
    try {
      await authApi.disableMfa(disablePassword, disableOtp);
      toast.success("MFA disabled successfully");
      setMfaEnabled(false);
      setShowDisableConfirm(false);
      setDisablePassword("");
      setDisableOtp("");
    } catch (e) {
      toast.error("Failed to disable MFA. Please check credentials.");
    } finally {
      setMfaLoading(false);
    }
  };

  if (!isTokenLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
        <div className="container mx-auto px-4 py-12 max-w-7xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-muted rounded-full" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Page Header */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Profile</h1>
              <p className="text-muted-foreground">
                Manage your account information
              </p>
            </div>
          </div>
        </div>

        {/* User Info Card */}
        <Card className="mb-6 shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Personal Information
                </CardTitle>
                <CardDescription className="mt-1">
                  Your account details and contact information
                </CardDescription>
              </div>
              {!isEditingName && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditName}
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  First Name
                </label>
                {isEditingName ? (
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={nameLoading}
                    placeholder="First name"
                  />
                ) : (
                  <div className="text-lg font-semibold text-foreground">
                    {user?.first_name || "Not set"}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Last Name
                </label>
                {isEditingName ? (
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={nameLoading}
                    placeholder="Last name"
                  />
                ) : (
                  <div className="text-lg font-semibold text-foreground">
                    {user?.last_name || "Not set"}
                  </div>
                )}
              </div>
            </div>

            {isEditingName && (
              <div className="flex gap-2">
                <Button onClick={handleSaveName} disabled={nameLoading}>
                  {nameLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleCancelEditName}
                  disabled={nameLoading}
                >
                  Cancel
                </Button>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Address
              </label>
              <div className="text-lg font-semibold text-foreground">
                {user?.email || "Not available"}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password + Security (MFA) cards:
            - Hidden entirely for sso_only orgs (password/MFA live in the IdP;
              the backend endpoints also 403 for those orgs).
            - Hidden for SSO-provisioned users in "both"-mode orgs
              (has_local_password=false); their MFA is managed by the IdP. */}
        {!isSSOOnlyOrg && (
          <>
        {user?.has_local_password !== false && (
          <>
        <ChangePasswordCard />

        <Card className="mb-6 shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Security
            </CardTitle>
            <CardDescription>Manage your account security</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  Multi-Factor Authentication
                  {mfaEnabled && (
                    <Badge variant="default" className="bg-green-600">
                      Enabled
                    </Badge>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {mfaEnabled
                    ? "Your account is secured with 2-factor authentication."
                    : "Add an extra layer of security to your account."}
                </p>
              </div>

              {mfaEnabled !== null ? (
                mfaEnabled ? (
                  showDisableConfirm ? (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        <Input
                          type="password"
                          value={disablePassword}
                          onChange={(e) => setDisablePassword(e.target.value)}
                          placeholder="Confirm Password"
                          className="w-full sm:w-40"
                        />
                        <Input
                          type="text"
                          value={disableOtp}
                          onChange={(e) => setDisableOtp(e.target.value)}
                          placeholder="OTP Code"
                          className="w-full sm:w-40"
                          maxLength={6}
                        />
                      </div>
                      <div className="flex gap-2 mt-2 sm:mt-0">
                        <Button
                          variant="destructive"
                          onClick={handleDisableMfa}
                          disabled={mfaLoading}
                        >
                          {mfaLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Confirm"
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setShowDisableConfirm(false);
                            setDisablePassword("");
                            setDisableOtp("");
                          }}
                          disabled={mfaLoading}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => setShowDisableConfirm(true)}
                    >
                      Disable MFA
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={handleEnableMfa}
                    disabled={mfaLoading}
                    className="w-full sm:w-auto"
                  >
                    {mfaLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending Email...
                      </>
                    ) : (
                      "Enable MFA"
                    )}
                  </Button>
                )
              ) : (
                <div className="h-10 w-24 bg-muted animate-pulse rounded" />
              )}
            </div>
          </CardContent>
        </Card>
          </>
        )}
          </>
        )}

        {/* API Key Management */}
        <ApiKeyManagement />

        {/* Roles Card */}
        <Card className="shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Access & Permissions
            </CardTitle>
            <CardDescription>
              Your roles and access level within the system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Primary Role */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-2 block">
                Primary Role
              </label>
              <Badge variant="default" className="text-sm px-3 py-1">
                {user?.role || "User"}
              </Badge>
            </div>

            {/* All Roles */}
            {user?.roles && user.roles.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  All Roles
                </label>
                <div className="flex flex-wrap gap-2">
                  {user.roles
                    .filter((role) => ["admin", "member", "org-owner"].includes(role))
                    .map((role, index) => (
                    <Badge key={index} variant="outline" className="text-sm">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Keycloak ID */}
            {user?.keycloak_id && (
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  User ID
                </label>
                <div className="text-sm font-mono text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                  {user.keycloak_id}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
